import { Hono } from "hono";
import type { Context } from "hono";
import type { ContentfulStatusCode } from "hono/utils/http-status";
import {
  executeOAuthProxy,
  executeDebugOAuthProxy,
  fetchOAuthMetadata,
  OAuthProxyError,
} from "../../utils/oauth-proxy.js";
import { ErrorCode, WebRouteError, mapRuntimeError } from "./errors.js";
import { bearerAuthMiddleware } from "../../middleware/bearer-auth.js";
import { guestRateLimitMiddleware } from "../../middleware/guest-rate-limit.js";
import { getRequestLogger } from "../../utils/request-logger.js";
import { classifyError } from "../../utils/error-classify.js";

const oauthWeb = new Hono();

function safeHostname(url: string | undefined): string {
  if (!url) return "unknown";
  try {
    return new URL(url).hostname || url;
  } catch {
    return url;
  }
}

// Require some form of bearer token (guest or WorkOS) on all OAuth proxy routes
oauthWeb.use("*", bearerAuthMiddleware);

// Rate limit guest users on OAuth proxy routes
oauthWeb.use("*", guestRateLimitMiddleware);

function statusToErrorCode(status: number): ErrorCode {
  if (status === 400) return ErrorCode.VALIDATION_ERROR;
  if (status === 401) return ErrorCode.UNAUTHORIZED;
  if (status === 403) return ErrorCode.FORBIDDEN;
  if (status === 404) return ErrorCode.NOT_FOUND;
  if (status === 429) return ErrorCode.RATE_LIMITED;
  if (status === 502) return ErrorCode.SERVER_UNREACHABLE;
  if (status === 504) return ErrorCode.TIMEOUT;
  return ErrorCode.INTERNAL_ERROR;
}

function webErrorCompat(c: Context, routeError: WebRouteError) {
  // TODO(hosted-v1.1): Remove `error` once clients migrate to `{ code, message }`.
  // This compatibility key exists for one release to avoid breaking callers that
  // still parse legacy `{ error }` payloads on oauth routes.
  return c.json(
    {
      code: routeError.code,
      message: routeError.message,
      error: routeError.message,
    },
    routeError.status as ContentfulStatusCode,
  );
}

function toRouteError(error: unknown): WebRouteError {
  if (error instanceof WebRouteError) {
    return error;
  }
  if (error instanceof OAuthProxyError) {
    return new WebRouteError(
      error.status,
      statusToErrorCode(error.status),
      error.message,
    );
  }
  return mapRuntimeError(error);
}

function getConvexHttpUrl(): string {
  const convexUrl = process.env.CONVEX_HTTP_URL;
  if (!convexUrl) {
    throw new WebRouteError(
      500,
      ErrorCode.INTERNAL_ERROR,
      "Server missing CONVEX_HTTP_URL configuration",
    );
  }

  return convexUrl;
}

async function proxyConvexOAuthPost(c: Context, path: string) {
  const convexUrl = getConvexHttpUrl();
  const authorization = c.req.header("authorization");
  const payload = await c.req.json();
  const response = await fetch(`${convexUrl}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(authorization ? { Authorization: authorization } : {}),
    },
    body: JSON.stringify(payload),
  });

  const bodyText = await response.text();
  return new Response(bodyText, {
    status: response.status,
    headers: {
      "Content-Type": response.headers.get("content-type") ?? "application/json",
    },
  });
}

/**
 * Proxy OAuth token exchange and client registration requests.
 * POST /api/web/oauth/proxy
 *
 * Mirrors /api/mcp/oauth/proxy with HTTPS-only + private IP blocking.
 * Body: { url: string, method?: string, body?: object, headers?: object }
 */
oauthWeb.post("/proxy", async (c) => {
  let proxyUrl: string | undefined;
  try {
    const { url, method, body, headers } = await c.req.json();
    proxyUrl = url;
    const result = await executeOAuthProxy({
      url,
      method,
      body,
      headers,
      httpsOnly: true,
    });
    return c.json(result);
  } catch (error) {
    getRequestLogger(c, "routes.web.oauth").event("mcp.oauth.proxy.failed", {
      targetUrlHost: safeHostname(proxyUrl),
      oauthPhase: "proxy",
      errorCode: classifyError(error),
      ...(error instanceof OAuthProxyError ? { statusCode: error.status } : {}),
    });
    return webErrorCompat(c, toRouteError(error));
  }
});

/**
 * Proxy OAuth metadata discovery requests.
 * GET /api/web/oauth/metadata?url=https://...
 *
 * Mirrors /api/mcp/oauth/metadata with HTTPS-only + private IP blocking.
 */
oauthWeb.get("/metadata", async (c) => {
  const metadataUrl = c.req.query("url");
  try {
    if (!metadataUrl) {
      throw new WebRouteError(
        400,
        ErrorCode.VALIDATION_ERROR,
        "Missing url parameter",
      );
    }

    const result = await fetchOAuthMetadata(metadataUrl, true);
    if ("status" in result && result.status !== undefined) {
      throw new WebRouteError(
        result.status,
        statusToErrorCode(result.status),
        `Failed to fetch OAuth metadata: ${result.status} ${result.statusText}`,
      );
    }

    return c.json(result.metadata);
  } catch (error) {
    getRequestLogger(c, "routes.web.oauth").event("mcp.oauth.proxy.failed", {
      targetUrlHost: safeHostname(metadataUrl),
      oauthPhase: "metadata",
      errorCode: classifyError(error),
      ...(error instanceof OAuthProxyError ? { statusCode: error.status } : {}),
    });
    return webErrorCompat(c, toRouteError(error));
  }
});

// Pure pass-through proxies to the matching Convex /web/oauth/* endpoints.
// Each handler is identical apart from the path, so register them in a loop
// rather than copy-pasting the try/catch shell four times.
const CONVEX_OAUTH_PROXY_PATHS = [
  "session",
  "tokens",
  "import-tokens",
  "client-secret",
] as const;

for (const path of CONVEX_OAUTH_PROXY_PATHS) {
  oauthWeb.post(`/${path}`, async (c) => {
    try {
      return await proxyConvexOAuthPost(c, `/web/oauth/${path}`);
    } catch (error) {
      return webErrorCompat(c, toRouteError(error));
    }
  });
}

// Local-mode token import — see backend `/web/oauth/import-tokens` for shape.
// The local CLI's `MCPOAuthProvider` uses this to push browser-side
// PKCE-exchanged tokens into Convex so the resolver can read them.
oauthWeb.post("/import-tokens", async (c) => {
  try {
    return await proxyConvexOAuthPost(c, "/web/oauth/import-tokens");
  } catch (error) {
    return webErrorCompat(c, toRouteError(error));
  }
});

/**
 * Debug proxy for OAuth flow visualization (hosted mode).
 * POST /api/web/oauth/debug/proxy
 *
 * Mirrors /api/mcp/oauth/debug/proxy with HTTPS-only + private IP blocking.
 * Body: { url: string, method?: string, body?: object, headers?: object }
 */
oauthWeb.post("/debug/proxy", async (c) => {
  let proxyUrl: string | undefined;
  try {
    const { url, method, body, headers } = await c.req.json();
    proxyUrl = url;
    const result = await executeDebugOAuthProxy({
      url,
      method,
      body,
      headers,
      httpsOnly: true,
    });
    return c.json(result);
  } catch (error) {
    getRequestLogger(c, "routes.web.oauth").event("mcp.oauth.proxy.failed", {
      targetUrlHost: safeHostname(proxyUrl),
      oauthPhase: "proxy",
      errorCode: classifyError(error),
      ...(error instanceof OAuthProxyError ? { statusCode: error.status } : {}),
    });
    return webErrorCompat(c, toRouteError(error));
  }
});

export default oauthWeb;
